/**
 * Gets the root step of a graph.
 */
const getRootStep = (graph = {}) => {
  const allSteps = new Set(Object.keys(graph));
  const childSteps = new Set(
    Object.values(graph)
      ?.flatMap((n) => [n.exit, n.child, ...(Array.isArray(n.branches) ? n.branches : [])])
      ?.filter(Boolean)
  );
  return [...allSteps].find((step) => !childSteps.has(step));
};

/**
 * Resolve the branch lanes to walk for one graph node.
 *
 * `graph.branches` IS the topology: a positional array of branch-lane head step ids whose slots
 * are position-locked and nullable (`[null, elseHead]` is a legal Else-without-Then). Every
 * positional edge is walked — the array is never sliced, filtered or compacted.
 *
 * Step-level `meta.branches` is a separate INTEGER count that "must not be read for topology"
 * (`docs/gravity/workflows/index.md`). Its only job here is PRESENTATION: padding lanes that are
 * declared but empty, so the canvas renders their add-step affordance. It can therefore only ever
 * grow the lane list, never shrink it.
 */
const fillBranches = (branches, presentationLaneCount = 0) => {
  const laneHeads = Array.isArray(branches) ? branches : [];
  const emptyLaneCount = Math.max(0, presentationLaneCount - laneHeads.length);
  return laneHeads.concat(new Array(emptyLaneCount).fill(undefined));
};

/**
 * Iterate through step graph and return the sequence of steps
 * to be rendered in the DOM.
 *
 *  - Level denotes the depth (or indent) of the step in the graph.
 *  - Child denotes whether the step is a child of another step,
 *    and thus if the line from its parent should be curved.
 */
export const flattenGraphSequence = (originalGraph, steps = [], parentStepId, includeFolders = false) => {
  const visited = new Set();

  const graph = { ...originalGraph };

  /**
   * Delete any invalid steps from the graph.
   * Todo: this should be server side and definitely not here.
   */
  for (const key in graph) {
    const x = graph[key];
    if (x.exit && !steps.find((n) => n?.id === x.exit)) {
      delete graph[key].exit;
    }
    if (x.exit && key === x.exit) {
      delete graph[key].exit;
    }
    if (x.child && key === x.child) {
      delete graph[key].child;
    }
    if (x.child && !steps.find((n) => n?.id === x.child)) {
      delete graph[key].child;
    }
    // this isn't right, branch index is important.
    // x.branches = x.branches?.filter((b) => steps.find((n) => n?.id === b)) || [];
  }

  const iterate = ({ step, level = 0, isChild = false, isBranchChild = false, branchIndex, branchParentLines = [], branchParentInfo = [], exitLines = [], branchLines = [], parents = [], branchSiblings }) => {
    if (!step || visited.has(step)) {
      return [];
    }
    visited.add(step);

    if (!graph[step]) {
      return [];
    }

    const { child, branches, exit, type } = graph[step];

    const stepObject = steps.find((n) => n?.id === step);
    const stepBranches = stepObject?.meta?.branches || 0;
    const newExitLines = [...exitLines, !!exit];
    const hasFilledSiblings = branchSiblings?.slice(branchIndex + 1).filter((n) => !!n).length > 0;
    const needsBranchLine = isBranchChild && !exit && hasFilledSiblings;

    const newBranchLines = [...branchLines, !!needsBranchLine];
    const parent = {
      id: step,
      childId: child,
      exitId: exit,
      hasExit: !!exit,
      level
    };

    // Branches are children of logic, such as if statements or switches.
    const filledBranchChildren = fillBranches(branches, stepBranches);

    const branchChildren =
      filledBranchChildren
        ?.flatMap((n, idx) =>
          n === undefined
            ? undefined
            : iterate({
                step: n,
                level: level + 1,
                isBranchChild: true,
                isChild: true,
                branchIndex: idx,
                branchSiblings: filledBranchChildren,
                exitLines: newExitLines,
                branchParentLines: [...branchParentLines, true],
                branchParentInfo: [...branchParentInfo, { isBranch: true, branchIndex: idx, branchSiblings: filledBranchChildren }],
                branchLines: newBranchLines,
                parents: [...parents, parent]
              })
        )
        .filter((n) => !!n) || [];

    // Children are steps executed one or more times before moving on to the exit step,
    // such as a for loop or a folder.
    const childChildren = child
      ? iterate({
          step: child,
          level: level + 1,
          isChild: true,
          exitLines: newExitLines,
          branchParentLines: [...branchParentLines, false],
          branchParentInfo: [...branchParentInfo, { isBranch: false }],
          branchLines: newBranchLines,
          parents: [...parents, parent]
        })
      : [];

    // Exit steps are executed after the current step is complete.
    // These are always on the same level as the current step.
    const exitChildren = exit
      ? iterate({
          step: exit,
          level,
          isChild: false,
          isBranchChild,
          branchSiblings,
          branchIndex,
          exitLines: [...exitLines],
          branchParentLines: [...branchParentLines],
          branchParentInfo: [...branchParentInfo],
          branchLines: [...branchLines],
          parents: [...parents]
        })
      : [];

    const finalExitLines = [...newExitLines];
    if (exitChildren?.length < 1) {
      finalExitLines[level] = false;
    }

    const finalBranchLines = [...newBranchLines];
    // if (branchChildren?.length < 1) {
    //   finalBranchLines[level] = false;
    // }

    const entry = {
      id: step,
      exitLines: finalExitLines,
      branchParentLines: [...branchParentLines],
      branchParentInfo: [...branchParentInfo],
      level,
      type,
      isChild,
      isBranchChild,
      branchIndex,
      branchSiblings,
      branchLines: [...finalBranchLines],
      childCount:
        graph[step]?.type !== 'folder'
          ? 1 //
          : [...branchChildren, ...childChildren].reduce((acc, c) => acc + c?.childCount, 0),
      childId: child,
      branches: [...(filledBranchChildren || [])],
      exitId: exit,
      parents: [...(parents || [])].reverse()
    };

    return [
      entry, //
      ...((type === 'folder' && !includeFolders ? [] : branchChildren) || []),
      ...((type === 'folder' && !includeFolders ? [] : childChildren) || []),
      ...exitChildren
    ];
  };
  if (parentStepId && !graph[parentStepId]?.child) {
    return [];
  }
  const rootStep = graph?.[parentStepId]?.child || getRootStep(graph);
  return iterate({ step: rootStep });
};
